import {useEffect, useState} from 'react';

/**
 * Generates a simple hash from a string.
 * @param {string} str - The string to hash.
 * @returns {string} - The generated hash.
 */
const generateHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return `script-${hash}`;
};

/**
 * useScript - Custom hook for loading external scripts dynamically.
 *
 * @param {string} src - The source URL of the script to be loaded.
 * @param {Object} options - Options for loading the script.
 * @param {Object} attributes - Attributes to be added to the script tag.
 * @returns {boolean} - A state indicating whether the script has loaded.
 */
export const useScript = (src, options = {}, attributes = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const opts = {
      id: options.id || generateHash(src),
      appendTo: options.appendTo || document.body,
      keep: options.keep || true,
    };

    let script = document.getElementById(opts.id);

    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.id = opts.id;
      // Set async = true as the default value
      const fullAttributes = {async: true, ...attributes};
      // Set provided attributes
      Object.keys(fullAttributes).forEach((key) => {
        const value = fullAttributes[key];
        if (typeof value === 'boolean') {
          // Handle boolean attributes
          if (value) {
            script.setAttribute(key, '');
          }
        } else {
          script.setAttribute(key, value);
        }
      });
      opts.appendTo.appendChild(script);
    } else {
      setIsLoaded(script.hasAttribute('data-loaded'));
    }

    const onScriptLoad = () => {
      script.setAttribute('data-loaded', '');
      setIsLoaded(true);
    };
    const onScriptError = () => {
      evite.error(`Failed to load ${src}`);
      setIsLoaded(false);
    };

    script.addEventListener('load', onScriptLoad);
    script.addEventListener('error', onScriptError);

    return () => {
      script.removeEventListener('load', onScriptLoad);
      script.removeEventListener('error', onScriptError);

      if (!opts.keep) {
        script.remove();
      }
    };
  }, [src, options, attributes]);

  return isLoaded;
};
